// The end-to-end channel to the paired machine: envelopes sealed to it and opened from it only,
// with per-peer counters committed to IndexedDB before anything is sent or accepted.
// The mock daemon (src/mock) implements the same Channel interface, so the UI cannot tell them apart.
import {
  importEncPub,
  importSigPub,
  open,
  seal,
  type ClientFrame,
  type DaemonMessage,
  type DeviceMessage,
  type Envelope,
  type Member,
  type RelayFrame,
} from "@whistle/protocol";
import { acceptInCounter, getInCounter, reserveOutCounter } from "./db";
import type { DeviceIdentity } from "./identity";
import type { ConnStatus, PairingRecord } from "./model";
import { RelayClient } from "./relay";

export type ChannelEvents = {
  status: (status: ConnStatus) => void;
  ready: (members: Member[]) => void;
  presence: (p: { id: string; online: boolean; lastSeen: number }) => void;
  message: (msg: DaemonMessage) => void;
  /** The relay no longer accepts this device. The phone shows it as removed but deletes nothing on the relay's word. */
  removed: () => void;
  pushResult: (r: { status: number; gone?: boolean }) => void;
  /** Something arrived that failed verification; shown nowhere, logged for diagnostics. */
  dropped: (reason: string) => void;
};

export interface Channel {
  start(): void;
  stop(): void;
  readonly connected: boolean;
  /** Drop the connection and open a fresh one now: the app suspects the old one died without saying so. */
  reconnect(): void;
  /** Seal and send an application message to the paired machine. Resolves false if not connected. */
  send(msg: DeviceMessage): Promise<boolean>;
  /** Send a plain relay frame (registerPush). */
  sendFrame(frame: ClientFrame): boolean;
  on<K extends keyof ChannelEvents>(event: K, fn: ChannelEvents[K]): () => void;
}

export class Emitter<E extends Record<string, (...args: never[]) => void>> {
  private readonly listeners = new Map<keyof E, Set<E[keyof E]>>();
  on<K extends keyof E>(event: K, fn: E[K]): () => void {
    let set = this.listeners.get(event);
    if (!set) this.listeners.set(event, (set = new Set()));
    set.add(fn);
    return () => set?.delete(fn);
  }
  emit<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void {
    for (const fn of this.listeners.get(event) ?? []) (fn as (...a: Parameters<E[K]>) => void)(...args);
  }
}

/** Opens one envelope from the paired machine, checking and committing the counter. Shared with pairing. */
export async function openFromMachine<T = DaemonMessage>(env: Envelope, pairing: Pick<PairingRecord, "machineId" | "machine" | "deviceId">, identity: DeviceIdentity, now = Date.now()): Promise<T> {
  if (env.from !== pairing.machineId) throw new Error("unknown sender");
  const senderSigPub = await importSigPub(pairing.machine.sigPub);
  const peer = { machineId: pairing.machineId, deviceId: pairing.deviceId };
  const lastCtr = await getInCounter(peer);
  const { message, ctr } = await open<T>(env, { self: pairing.deviceId, senderSigPub, recipientEncPriv: identity.identity.enc.privateKey, lastCtr, now });
  await acceptInCounter(peer, ctr);
  return message;
}

export async function sealToMachine(msg: DeviceMessage, pairing: Pick<PairingRecord, "machineId" | "machine" | "deviceId">, identity: DeviceIdentity, now = Date.now()): Promise<Envelope> {
  const ctr = await reserveOutCounter({ machineId: pairing.machineId, deviceId: pairing.deviceId });
  const recipientEncPub = await importEncPub(pairing.machine.encPub);
  return seal({ from: pairing.deviceId, to: pairing.machineId, ctr, message: msg, senderSigPriv: identity.identity.sig.privateKey, recipientEncPub, now });
}

export type RelayChannelOptions = {
  pairing: PairingRecord;
  identity: DeviceIdentity;
  WebSocketImpl?: typeof WebSocket;
  now?: () => number;
  doc?: Document;
  win?: Window;
};

export class RelayChannel implements Channel {
  private readonly events = new Emitter<ChannelEvents>();
  private readonly client: RelayClient;
  private queue: Promise<void> = Promise.resolve();
  private readonly opts: RelayChannelOptions;

  constructor(opts: RelayChannelOptions) {
    this.opts = opts;
    this.client = new RelayClient({
      relayOrigin: opts.pairing.relayOrigin,
      accountId: opts.pairing.accountId,
      deviceId: opts.pairing.deviceId,
      signKey: opts.identity.identity.sig.privateKey,
      WebSocketImpl: opts.WebSocketImpl,
      doc: opts.doc,
      win: opts.win,
      onFrame: (f) => this.onFrame(f),
      onStatus: (s) => {
        if (s === "connected") return; // "ready" carries the members; status follows from it
        this.events.emit("status", s === "closed" ? "idle" : s);
      },
      // A first 4001 can be a slow handshake ("auth timeout", "challenge expired"), and removal ends the session, so only
      // a second one in a row, with no successful auth between, counts as removed (security review 2, S2).
      onAuthRejected: (inARow) => {
        if (inARow < 2) return "retry";
        this.events.emit("removed");
        return "stop";
      },
      onRemoved: () => this.events.emit("removed"),
    });
  }

  get connected(): boolean {
    return this.client.connected;
  }

  start(): void {
    this.client.start();
  }

  stop(): void {
    this.client.stop();
  }

  reconnect(): void {
    this.client.restart();
  }

  on<K extends keyof ChannelEvents>(event: K, fn: ChannelEvents[K]): () => void {
    return this.events.on(event, fn);
  }

  sendFrame(frame: ClientFrame): boolean {
    return this.client.send(frame);
  }

  async send(msg: DeviceMessage): Promise<boolean> {
    if (!this.client.connected) return false;
    const env = await sealToMachine(msg, this.opts.pairing, this.opts.identity, this.opts.now?.());
    return this.client.send({ t: "msg", to: this.opts.pairing.machineId, env });
  }

  private onFrame(frame: RelayFrame): void {
    switch (frame.t) {
      case "ready":
        this.events.emit("status", "connected");
        this.events.emit("ready", frame.members);
        return;
      case "presence":
        this.events.emit("presence", { id: frame.id, online: frame.online, lastSeen: frame.lastSeen });
        return;
      case "pushResult":
        this.events.emit("pushResult", { status: frame.status, gone: frame.gone });
        return;
      case "msg":
        // Envelopes are opened one at a time so counters are checked and committed in order.
        this.queue = this.queue.then(async () => {
          try {
            if (frame.from !== this.opts.pairing.machineId || frame.env.from !== this.opts.pairing.machineId) throw new Error("not from the paired machine");
            const msg = await openFromMachine(frame.env, this.opts.pairing, this.opts.identity, this.opts.now?.());
            if (!msg || typeof msg !== "object" || typeof (msg as { type?: unknown }).type !== "string") throw new Error("not a message");
            this.events.emit("message", msg);
          } catch (e) {
            this.events.emit("dropped", e instanceof Error ? e.message : String(e));
          }
        });
        return;
      case "error":
        this.events.emit("dropped", `relay error ${frame.code}: ${frame.message}`);
        return;
      default:
        return;
    }
  }
}
